import type { IAccountsView, IAuthCredentials } from '@shared/services';
import { publicApi } from '../../client';

/**
 * `IAuthCredentials` already carries the optional `accountsProof`: it is omitted on the first call and
 * sent on the SECOND call (after the MFA ceremony) to reveal the account list without re-challenging.
 */
export type CheckCredentialsRequestParams = IAuthCredentials;

/**
 * An MFA challenge raised at `check-credentials`: the account list is withheld (empty `list`) and an
 * `outcome` is present. Mirrors the `login.request.ts` guards. Clients MUST branch on `outcome` before
 * inspecting `list` — an empty `list` WITHOUT an `outcome` still means "no accounts".
 */
export const isMfaChallenge = (view: IAccountsView): boolean =>
  view.outcome === 'mfa_required' || view.outcome === 'mfa_enrollment_required';

/** A challenge for an identity that must VERIFY an already-configured factor (handled inline). */
export const isMfaVerifyChallengeView = (view: IAccountsView): boolean => view.outcome === 'mfa_required';

/** A challenge for an identity that must SET UP a factor first (an inline login-wizard step). */
export const isMfaEnrollmentChallengeView = (view: IAccountsView): boolean =>
  view.outcome === 'mfa_enrollment_required';

export async function checkCredentialsRequest(params: CheckCredentialsRequestParams): Promise<IAccountsView> {
  const { data } = await publicApi.post('/api/v1/auth/check-credentials', params);

  return data;
}
